import dayjs from 'dayjs';

import { localTime } from '@/utils/utc-time';

export type DateTimeRangePickerValue =
  | { start_time?: string; end_time?: string; duration?: string }
  | undefined;

export type DurationUnit = 'y' | 'M' | 'w' | 'd' | 'h' | 'm' | 's' | 'ms';

const VALID_UNITS: DurationUnit[] = ['y', 'M', 'w', 'd', 'h', 'm', 's', 'ms'];
function isValidUnit(unit: string): unit is DurationUnit {
  return VALID_UNITS.includes(unit as DurationUnit);
}

export function parseRelativeDate(
  input: string | null | undefined
): { value: number; unit: DurationUnit } | null {
  if (input == null) return null;
  const result = input.match(/(?<valueText>[0-9]+)(?<unit>[a-zA-Z]+)/);
  const { valueText, unit } = result?.groups ?? {};
  const value = Math.round(Number.parseInt(valueText, 10));
  if (!valueText || Number.isNaN(value) || !unit) return null;
  if (!isValidUnit(unit)) return null;

  return { value, unit };
}

const UNIT_LABELS: Record<DurationUnit, string> = {
  y: 'year',
  M: 'month',
  w: 'week',
  d: 'day',
  h: 'hour',
  m: 'minute',
  s: 'second',
  ms: 'millisecond',
};

export function formatDuration(input: { value: number; unit: DurationUnit }): {
  label: string;
  value: string;
} {
  let unitLabel = UNIT_LABELS[input.unit];
  if (input.value > 1) unitLabel += 's';
  return {
    label: `Last ${input.value} ${unitLabel}`,
    value: `${input.value}${input.unit}`,
  };
}

export function getDateTimeRangeLabel(value: DateTimeRangePickerValue) {
  if (value?.duration) {
    const parsed = parseRelativeDate(value.duration);
    if (!parsed) return 'All time';
    return formatDuration(parsed).label;
  }

  const start = value?.start_time ? localTime(value?.start_time) : undefined;
  const end = value?.end_time ? localTime(value?.end_time) : undefined;
  const now = dayjs();

  if (start && !end) {
    if (start.format('MM/DD/YYYY') === now.format('MM/DD/YYYY')) {
      return `Later than ${start.format('hh:mm:ss A')}`;
    }

    return `Later than ${start.format('MM/DD/YYYY, hh:mm:ss A')}`;
  }

  if (!start && end) {
    if (end.format('MM/DD/YYYY') === now.format('MM/DD/YYYY')) {
      return `Earlier than ${end.format('hh:mm:ss A')}`;
    }

    return `Earlier than ${end.format('MM/DD/YYYY, hh:mm:ss A')}`;
  }

  if (start && end) {
    if (start.format('MM/DD/YYYY') === end.format('MM/DD/YYYY')) {
      if (start.format('MM/DD/YYYY') === now.format('MM/DD/YYYY')) {
        return `${start.format('hh:mm:ss A')} - ${end.format('hh:mm:ss A')}`;
      }

      return `${start.format('MM/DD/YYYY hh:mm:ss A')} - ${end.format(
        'hh:mm:ss A'
      )}`;
    }

    return `${start.format('MM/DD/YYYY, hh:mm:ss A')} - ${end.format(
      'MM/DD/YYYY, hh:mm:ss A'
    )}`;
  }

  return 'All time';
}
